import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal, Label } from "@/components/ui-bits";
import { api, useAuth } from "@/lib/auth";

export function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const done = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sessionId = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    if (!sessionId) return navigate("/portal/login", { replace: true });
    api("/auth/session", { method: "POST", body: { session_id: sessionId } })
      .then((user) => {
        setUser(user);
        window.history.replaceState(null, "", "/portal");
        navigate("/portal", { replace: true, state: { user } });
      })
      .catch((e) => setError(e.message));
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen grid place-items-center px-6 text-center" data-testid="auth-callback">
      <p className="text-mute">{error ? `Sign-in failed: ${error}` : "Signing you in…"}</p>
    </div>
  );
}

export function ProtectedRoute({ children, admin = false }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <div className="min-h-screen grid place-items-center text-mute" data-testid="auth-loading">Checking your session…</div>;
  if (!user) return <Navigate to="/portal/login" replace state={{ from: location.pathname }} />;
  if (admin && user.role !== "admin") return <Navigate to="/portal" replace />;
  return children;
}

function GoogleButton() {
  const start = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={start} className="btn-ghost w-full justify-center gap-3" data-testid="login-google-button">
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.5 13.3l7.8 6.1C12.2 13.6 17.6 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"/><path fill="#FBBC05" d="M10.3 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.1.8-4.6l-7.8-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.5 10.7l7.8-6.1z"/><path fill="#34A853" d="M24 48c6.2 0 11.6-2 15.4-5.6l-7.5-5.8c-2.1 1.4-4.8 2.3-7.9 2.3-6.4 0-11.8-4.1-13.7-9.9l-7.8 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
      Continue with Google
    </button>
  );
}

export default function LoginPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/portal" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body = mode === "login" ? { email: form.email, password: form.password } : form;
      const u = await api(`/auth/${mode}`, { method: "POST", body });
      setUser(u);
      navigate("/portal", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <>
      <Seo title="Patient Portal Sign In | Androgen Health" description="Sign in to the Androgen Health patient portal to see your blood results with plain-English explanations and monitoring reminders." />
      <PageHero label="Patient portal" title={<>Your results,<br /><span className="serif-it text-teal">explained.</span></>} lede="Sign in to track your blood results over time, with each marker explained in plain English and every clinic-entered result verified." safety={false} />
      <section className="pb-24 px-5 sm:px-8">
        <div className="max-w-md mx-auto lg:mx-0">
          <Reveal>
            <form onSubmit={submit} className="card-line rounded-2xl p-6 sm:p-8 space-y-4" data-testid="login-form">
              <div className="inline-flex rounded-full border border-line p-0.5">
                {["login", "register"].map((m) => (
                  <button key={m} type="button" onClick={() => setMode(m)} className={`px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider transition-colors ${mode === m ? "bg-teal text-ink" : "text-mute"}`} data-testid={`login-mode-${m}`}>
                    {m === "login" ? "Sign in" : "Create account"}
                  </button>
                ))}
              </div>
              {mode === "register" && (
                <div>
                  <label htmlFor="pl-name" className="mono-label text-dusk block mb-2">Full name</label>
                  <input id="pl-name" required className="field" value={form.name} onChange={set("name")} data-testid="login-name" />
                </div>
              )}
              <div>
                <label htmlFor="pl-email" className="mono-label text-dusk block mb-2">Email</label>
                <input id="pl-email" type="email" required className="field" value={form.email} onChange={set("email")} data-testid="login-email" />
              </div>
              <div>
                <label htmlFor="pl-pass" className="mono-label text-dusk block mb-2">Password {mode === "register" && <span className="text-dusk">(8+ characters)</span>}</label>
                <input id="pl-pass" type="password" required minLength={8} className="field" value={form.password} onChange={set("password")} data-testid="login-password" />
              </div>
              {error && <p className="text-rose text-sm" data-testid="login-error">{error}</p>}
              {mode === "login" && <p className="text-xs text-right"><Link to="/portal/forgot" className="text-mute hover:text-teal transition-colors" data-testid="login-forgot-link">Forgotten your password?</Link></p>}
              <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="login-submit">
                {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
              </button>
              <div className="flex items-center gap-3 text-dusk text-xs"><span className="h-px bg-line flex-1" />or<span className="h-px bg-line flex-1" /></div>
              <GoogleButton />
              <p className="text-xs text-dusk leading-relaxed">Your portal shows information only. It does not replace clinical review, and urgent concerns should go to 111 or 999. <Link to="/privacy" className="text-teal hover:underline">Privacy</Link>.</p>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}

export function ForgotPage() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    setStatus("busy");
    try { await api("/auth/forgot", { method: "POST", body: { email } }); } catch {}
    setStatus("sent");
  };
  return (
    <>
      <Seo title="Reset Password | Androgen Health Portal" description="Request a password reset link for the Androgen Health patient portal." />
      <PageHero label="Patient portal" title="Forgotten password." lede="Enter your email and, if an account exists, we'll send a single-use link that expires in one hour." safety={false} />
      <section className="pb-24 px-5 sm:px-8"><div className="max-w-md mx-auto lg:mx-0"><Reveal>
        {status === "sent" ? (
          <div className="card-line rounded-2xl p-8" data-testid="forgot-sent"><p className="text-bone/90">If an account exists for <span className="text-teal">{email}</span>, a reset link is on its way. Check your spam folder if it hasn't arrived in a few minutes.</p><Link to="/portal/login" className="btn-ghost mt-6" data-testid="forgot-back">Back to sign in</Link></div>
        ) : (
          <form onSubmit={submit} className="card-line rounded-2xl p-6 sm:p-8 space-y-4" data-testid="forgot-form">
            <label className="block"><span className="mono-label text-dusk block mb-2">Email</span><input type="email" required className="field" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" /></label>
            <button type="submit" disabled={status === "busy"} className="btn-primary w-full justify-center" data-testid="forgot-submit">{status === "busy" ? "Sending…" : "Send reset link"}</button>
            <p className="text-xs text-dusk">Signed up with Google? Just use "Continue with Google" — there is no password to reset.</p>
          </form>
        )}
      </Reveal></div></section>
    </>
  );
}

export function ResetPage() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const token = new URLSearchParams(useLocation().search).get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return setError("Passwords do not match");
    setBusy(true); setError("");
    try {
      const u = await api("/auth/reset", { method: "POST", body: { token, password } });
      setUser(u);
      navigate("/portal", { replace: true });
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  return (
    <>
      <Seo title="Choose a New Password | Androgen Health Portal" description="Set a new password for the Androgen Health patient portal." />
      <PageHero label="Patient portal" title="Choose a new password." lede="Use at least 8 characters. You'll be signed in straight away." safety={false} />
      <section className="pb-24 px-5 sm:px-8"><div className="max-w-md mx-auto lg:mx-0"><Reveal>
        <form onSubmit={submit} className="card-line rounded-2xl p-6 sm:p-8 space-y-4" data-testid="reset-form">
          {!token && <p className="text-rose text-sm" data-testid="reset-missing-token">This link is missing its token. <Link to="/portal/forgot" className="text-teal hover:underline">Request a new one</Link>.</p>}
          <label className="block"><span className="mono-label text-dusk block mb-2">New password</span><input type="password" required minLength={8} className="field" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" /></label>
          <label className="block"><span className="mono-label text-dusk block mb-2">Confirm password</span><input type="password" required minLength={8} className="field" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" /></label>
          {error && <p className="text-rose text-sm" data-testid="reset-error">{error}</p>}
          <button type="submit" disabled={busy || !token} className="btn-primary w-full justify-center" data-testid="reset-submit">{busy ? "Saving…" : "Set new password"}</button>
        </form>
      </Reveal></div></section>
    </>
  );
}
